import { omit } from "lodash";
import { toast } from "react-toastify";
import { DEFAULT_EVENT_IMAGE_URL } from "../../../utils/constants";
import {
  deepEmptyStringsToUndefined,
  formatDate,
  handleError,
} from "../../../utils/helpers";
import { BaseApiResponse, QueryParams } from "../../../utils/types";
import { baseApi } from "../../api";
import { authSliceActions } from "../auth/auth.slice";
import {
  AcceptCoOrganizerInvite,
  AssignEventDiscountPayload,
  AvailableCategoriesResponse,
  BuyTicketsPayload,
  BuyTicketsResponse,
  CreateDiscountPayload,
  CreateDiscountResponse,
  CreateEvent,
  CreateEventResponse,
  CreateTicket,
  DiscountLogsResponse,
  DuplicateEventPayload,
  EditEventResponse,
  EditEventTicketResponse,
  EventStatus,
  FindEventsQuery,
  GetDiscountResponse,
  GetDiscountsResponse,
  GetDiscoutQuery,
  GetEmailBlastResponse,
  GetEventGuestResponse,
  GetEventGuestsResponse,
  GetEventResponse,
  GetEventsQuery,
  GetEventsResponse,
  GetEventTicketsBreakdownResponse,
  GetEventTicketsResponse,
  HoldTicketsPayload,
  HoldTicketsResponse,
  InviteCoOrganizer,
  MailingListSubscribePayload,
  RemoveCoOrganizer,
  SendEmailBlast,
  UpdateCoOrganizer,
} from "./event.types";

export const eventApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    createEvent: builder.mutation<CreateEventResponse, CreateEvent>({
      query: (payload) => {
        return {
          url: "events",
          method: "POST",
          body: deepEmptyStringsToUndefined({
            ...payload,
            startDate: ["", undefined].includes(payload.startDate)
              ? undefined
              : `${formatDate(payload.startDate, "YYYY-MM-DD")}T00:00:00Z`,
            endDate: ["", undefined].includes(payload.endDate)
              ? undefined
              : `${formatDate(payload.endDate, "YYYY-MM-DD")}T00:00:00Z`,
            image:
              payload.image === "" ? DEFAULT_EVENT_IMAGE_URL : payload.image,
            tickets: payload.tickets.map((t) => omit(t, ["quantity"])),
          }),
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.status === "success") {
            dispatch(authSliceActions.markCreateEvent());
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    editEvent: builder.mutation<
      CreateEventResponse,
      { id: string; payload: Partial<CreateEvent> }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/${id}`,
          method: "PUT",
          body: deepEmptyStringsToUndefined({
            ...payload,
            startDate: ["", undefined].includes(payload.startDate)
              ? undefined
              : `${formatDate(payload.startDate, "YYYY-MM-DD")}T00:00:00Z`,
            endDate: ["", undefined].includes(payload.startDate)
              ? undefined
              : `${formatDate(payload.endDate, "YYYY-MM-DD")}T00:00:00Z`,
            image:
              payload.image === "" ? DEFAULT_EVENT_IMAGE_URL : payload.image,
            tickets: payload?.tickets
              ? payload.tickets.map((t) => omit(t, ["quantity"]))
              : undefined,
          }),
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateEventStatus: builder.mutation<
      EditEventResponse,
      { id: string; payload: { status: EventStatus | "unarchive" } }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/${id}/status`,
          method: "PATCH",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getEvents: builder.query<GetEventsResponse, Partial<GetEventsQuery>>({
      query: ({ ...params }) => {
        return {
          url: "events",
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    getEvent: builder.query<
      GetEventResponse,
      { id?: string; slug?: string; authenticated?: boolean }
    >({
      query: (params) => {
        return {
          url: `events/${params.id ?? encodeURIComponent(params.slug ?? "")}${params.slug ? "/details" : ""}${params.authenticated ? "/authenticated-route" : ""}`,
          method: "GET",
          params: undefined,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    getEventTickets: builder.query<
      GetEventTicketsResponse,
      QueryParams & { id?: string; slug?: string }
    >({
      query: ({ id, slug, ...params }) => {
        return {
          url: `events/${slug ? "slug/" : ""}${id ?? slug}/tickets`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    getEventTicketsBreakdown: builder.query<
      GetEventTicketsBreakdownResponse,
      QueryParams & { id: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `events/${id}/ticket-breakdown`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    addEventTicket: builder.mutation<
      BaseApiResponse,
      { id: string; payload: CreateTicket }
    >({
      query: ({ payload, id }) => {
        return {
          url: `events/${id}/tickets`,
          method: "POST",
          body: omit(payload, ["quantity"]),
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          toast.success(resp.data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    editEventTicket: builder.mutation<
      EditEventTicketResponse,
      { id: string; payload: Partial<CreateTicket> }
    >({
      query: ({ id, payload }) => {
        return {
          url: `events/ticket/${id}`,
          method: "PUT",
          body: { ...omit(payload, ["quantity"]) },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    deleteEventTicket: builder.mutation<BaseApiResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `events/ticket/${id}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getEventGuests: builder.query<
      GetEventGuestsResponse,
      QueryParams & { id: string; search: string; status?: string }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `guests/${id}`,
          method: "GET",
          params: {
            ...params,
            search: params.search === "" ? undefined : params.search,
            status: params.status,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    getEventGuest: builder.query<GetEventGuestResponse, { id: string }>({
      query: (params) => {
        return {
          url: `guests/${params.id}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    checkGuestIn: builder.mutation<BaseApiResponse, { id: string }>({
      query: (payload) => {
        return {
          url: `guests/${payload.id}/check-in`,
          method: "PATCH",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    buyTicktes: builder.mutation<
      BuyTicketsResponse,
      { id: string; data: BuyTicketsPayload }
    >({
      query: (payload) => {
        return {
          url: `guests/${payload.id}/buy-ticket`,
          method: "POST",
          body: payload.data,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    createDiscount: builder.mutation<
      CreateDiscountResponse,
      CreateDiscountPayload
    >({
      query: (payload) => {
        return {
          url: `events/discounts`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    editDiscount: builder.mutation<
      CreateDiscountResponse,
      { id: string; data: Partial<CreateDiscountPayload> }
    >({
      query: (payload) => {
        return {
          url: `events/discounts/${payload.id}`,
          method: "PATCH",
          body: payload.data,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    deleteDiscount: builder.mutation<BaseApiResponse, { id: string }>({
      query: (payload) => {
        return {
          url: `events/discounts/${payload.id}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getDiscounts: builder.query<GetDiscountsResponse, GetDiscoutQuery>({
      query: (params) => {
        return {
          url: `events/user/all/discounts`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    getDiscount: builder.query<
      GetDiscountResponse,
      { code: string; userId: string }
    >({
      query: (params) => {
        return {
          url: `guests/discount/${params.userId}/${encodeURIComponent(params.code)}/details`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    validateDiscount: builder.mutation<
      GetDiscountResponse,
      { code: string; userId: string; eventId?: string }
    >({
      query: ({ userId, code, ...params }) => {
        return {
          url: `guests/validate-discount/${userId}/${encodeURIComponent(code)}`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    fetchDiscountUsageLogs: builder.query<
      DiscountLogsResponse,
      { code: string; query: QueryParams }
    >({
      query: (params) => {
        return {
          url: `events/${encodeURIComponent(params.code)}/discount-logs`,
          method: "GET",
          params: { ...params.query },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    toggleAssignEventDiscount: builder.mutation<
      BaseApiResponse,
      AssignEventDiscountPayload
    >({
      query: (payload) => {
        return {
          url: `events/assign-discounts`,
          method: "PATCH",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === "success" && arg.status === "assign") {
            toast.success("Discount assigned to event");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    findEvents: builder.query<GetEventsResponse, FindEventsQuery>({
      query: ({ ...params }) => {
        return {
          url: "events/guests/explore",
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    fetchAvailableCategories: builder.query<AvailableCategoriesResponse, {}>({
      query: ({ ...params }) => {
        return {
          url: "/events/guest/explore/available-categories",
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    duplicateEvent: builder.mutation<BaseApiResponse, DuplicateEventPayload>({
      query: ({ id, ...payload }) => {
        return {
          url: `events/${id}/duplicate-event`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === "success") {
            toast.success("Event Duplicated");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    deleteEvent: builder.mutation<
      BaseApiResponse,
      {
        id: string;
      }
    >({
      query: ({ id }) => {
        return {
          url: `events/${id}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === "success") {
            toast.success("Event Deleted");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    cancelEvent: builder.mutation<
      BaseApiResponse,
      {
        id: string;
      }
    >({
      query: ({ id }) => {
        return {
          url: `events/cancel/${id}`,
          method: "PATCH",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === "success") {
            toast.success("Event Cancelled");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    subscribeToMailingList: builder.mutation<
      BaseApiResponse,
      MailingListSubscribePayload
    >({
      query: (payload) => {
        return {
          url: `guests/subscribe-to-events`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    holdTickets: builder.mutation<HoldTicketsResponse, HoldTicketsPayload>({
      query: (payload) => {
        return {
          url: `events/hold-tickets`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateHeldTickets: builder.mutation<
      HoldTicketsResponse,
      HoldTicketsPayload & { sessionId: string }
    >({
      query: (payload) => {
        return {
          url: `events/update-hold-tickets`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    releaseTickets: builder.mutation<BaseApiResponse, { sessionId: string }>({
      query: (payload) => {
        return {
          url: `events/tickets/${payload.sessionId}/release`,
          method: "PATCH",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    sendEmailBlast: builder.mutation<
      BaseApiResponse,
      { eventId: string; payload: SendEmailBlast }
    >({
      query: ({ eventId, payload }) => {
        return {
          url: `events/${eventId}/send-email-blast`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp?.data?.status === "success") {
            if (arg.payload.recipients === "all") {
              toast.success("Email sent to all guests");
              return;
            }
            if (arg.payload.sendType === "test") {
              toast.success("Test mail sent to your email");
              return;
            }
            if (arg.payload.sendType === "now") {
              toast.success(`Event sent to guests`);
              return;
            }
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getEmailBlasts: builder.query<
      GetEmailBlastResponse,
      QueryParams & { eventId: string }
    >({
      query: ({ ...params }) => {
        return {
          url: "events/user/email-blasts",
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),

    inviteEventCoOrganizer: builder.mutation<
      BaseApiResponse,
      { eventId: string; payload: InviteCoOrganizer }
    >({
      query: ({ payload, eventId }) => {
        return {
          url: `events/${eventId}/invite-coorganizer`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          toast.success(resp.data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateEventCoOrganizer: builder.mutation<
      BaseApiResponse,
      { eventId: string; payload: UpdateCoOrganizer }
    >({
      query: ({ payload, eventId }) => {
        return {
          url: `events/${eventId}/update-coorganizer-role`,
          method: "PATCH",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          toast.success(resp.data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    removeEventCoOrganizer: builder.mutation<
      BaseApiResponse,
      { eventId: string; payload: RemoveCoOrganizer }
    >({
      query: ({ payload, eventId }) => {
        return {
          url: `events/${eventId}/revoke-coorganizer`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          toast.success(resp.data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    acceptCoOrganizerInvite: builder.mutation<
      BaseApiResponse,
      { eventId: string; payload: AcceptCoOrganizerInvite }
    >({
      query: ({ payload, eventId }) => {
        return {
          url: `events/${eventId}/accept-coorganizer-invite`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),
  }),
});

export const {
  useCreateEventMutation,
  useEditEventMutation,
  useUpdateEventStatusMutation,
  useGetEventsQuery,
  useLazyGetEventQuery,
  useGetEventQuery,
  useGetEventTicketsQuery,
  useGetEventTicketsBreakdownQuery,
  useAddEventTicketMutation,
  useEditEventTicketMutation,
  useDeleteEventTicketMutation,
  useGetEventGuestsQuery,
  useGetEventGuestQuery,
  useCheckGuestInMutation,
  useBuyTicktesMutation,
  useCreateDiscountMutation,
  useEditDiscountMutation,
  useDeleteDiscountMutation,
  useGetDiscountsQuery,
  useGetDiscountQuery,
  useValidateDiscountMutation,
  useFetchDiscountUsageLogsQuery,
  useToggleAssignEventDiscountMutation,
  useFindEventsQuery,
  useFetchAvailableCategoriesQuery,
  useDuplicateEventMutation,
  useDeleteEventMutation,
  useCancelEventMutation,
  useSubscribeToMailingListMutation,
  useHoldTicketsMutation,
  useUpdateHeldTicketsMutation,
  useReleaseTicketsMutation,
  useSendEmailBlastMutation,
  useGetEmailBlastsQuery,
  useInviteEventCoOrganizerMutation,
  useUpdateEventCoOrganizerMutation,
  useRemoveEventCoOrganizerMutation,
  useAcceptCoOrganizerInviteMutation,
} = eventApi;
